import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Layers, Calculator, LayoutGrid, Wand2 } from "lucide-react";
import { useWizard } from "./WizardProvider";

// Raw, uncompressed original Buchanan facade — no AI modification (watermark region patched clean).
const HERO_BG = "/projects/buchanan/facade-original.jpg";

const PILLS = [
  { id: "pillars", label: "The Pillars", icon: Layers, target: "#pillars", gradient: "from-emerald-500/80 to-amber-500/80 border-white/30" },
  { id: "calculator", label: "Yield Calculator", icon: Calculator, target: "#calculator", gradient: "from-amber-500/80 to-orange-500/80 border-white/30" },
  { id: "services", label: "Services", icon: LayoutGrid, target: "#services", gradient: "from-slate-200/80 to-slate-400/80 border-white/30" },
  { id: "wizard", label: "Project Query Wizard", icon: Wand2, action: "wizard", gradient: "from-amber-400/90 to-yellow-500/90 border-white/40" },
];

export const Hero = () => {
  const lenis = useLenis();
  const { openWizard } = useWizard();

  const scrollTo = (id) => {
    if (lenis) lenis.scrollTo(id, { offset: -110, duration: 1.4 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const onPill = (p) => {
    if (p.action === "wizard") openWizard();
    else scrollTo(p.target);
  };

  return (
    <section id="top" className="min-h-screen bg-cover bg-center relative flex flex-col justify-between overflow-hidden bg-[#0A0E17]" data-testid="hero">
      <h1 className="sr-only">JAI HOMES — High-Yield Homes &amp; Commercial Builds Engineered for NSW Growth</h1>

      {/* Raw property backdrop — object position keeps sky + full facade in frame */}
      <img
        src={HERO_BG}
        alt="74 Camargo Cct, Buchanan — completed JAI HOMES residential facade"
        fetchPriority="high"
        className="absolute inset-0 w-full h-full object-cover object-[50%_8%]"
        data-testid="hero-bg"
      />
      {/* Readability gradients only — architecture stays unobscured */}
      <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-[#0A0E17]/55 via-[#0A0E17]/20 to-transparent pointer-events-none" />
      {/* Luxury bottom vignette — smooths the lawn into a sleek dark glass transition, pills pop */}
      <div className="absolute inset-x-0 bottom-0 h-[30%] bg-gradient-to-t from-slate-950/80 via-slate-950/40 to-transparent pointer-events-none" data-testid="hero-vignette" />

      {/* 3D glass mold logo — upper sky, centered, clear of the roofline at all viewport sizes.
          NOTE: keep centering (left-1/2 -translate-x-1/2) on this plain wrapper div — framer-motion
          animate transforms override Tailwind translate utilities, so motion elements must never own it. */}
      <div className="absolute left-1/2 -translate-x-1/2 top-12 sm:top-[3vh] xl:top-[4vh] z-30" style={{ perspective: 1000 }}>
        <motion.div
          initial={{ opacity: 0, y: -18, rotateX: 10 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
        >
          <motion.div
            animate={{ y: [-5, 5, -5], rotateX: [3, -2, 3], rotateY: [-2.5, 2.5, -2.5] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ rotateX: 4, z: 10, scale: 1.03 }}
            className="cursor-default backdrop-blur-xl bg-white/15 border-2 border-white/30 shadow-[0_25px_60px_rgba(251,146,60,0.35)] rounded-3xl px-6 py-3 sm:px-10 sm:py-5 transition-all duration-300"
            data-testid="hero-logo-mold"
          >
            <img
              src="/images/jai-homes-logo.png"
              alt="JAI HOMES — Where Dreams Become Reality"
              draggable={false}
              className="w-[170px] lg:w-[190px] xl:w-[240px] h-auto select-none drop-shadow-[0_0_35px_rgba(249,115,22,0.65)]"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Driveway pill navigation */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.5, ease: "easeOut" }}
        className="absolute bottom-28 sm:bottom-9 inset-x-0 z-10 px-4 flex justify-center"
      >
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-row sm:gap-4" data-testid="hero-pill-bar">
          {PILLS.map((p) => (
            <button
              key={p.id}
              onClick={() => onPill(p)}
              data-testid={`hero-pill-${p.id}`}
              className={`relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 sm:px-8 sm:py-3.5 font-display text-[12px] sm:text-sm font-bold tracking-wide text-slate-950 border shadow-2xl backdrop-blur-md bg-gradient-to-r ${p.gradient} cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-[0_10px_30px_rgba(249,115,22,0.35)] before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/45 before:via-white/5 before:to-transparent before:pointer-events-none`}
            >
              <p.icon size={16} className="relative shrink-0" />
              <span className="relative">{p.label}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
